/**
 * HrPersonnelAppointmentPage 컴포넌트
 *
 * V51: 인사발령 페이지 (4탭 구조)
 *
 * **탭 구성:**
 * 1. 진급후보 산출 - 기존 진급 후보자 자동 산출 기능
 * 2. 임명 - 발령 처리 (추후 구현)
 * 3. 인사발령 예약 - 예약 발령 관리 (추후 구현)
 * 4. 히스토리 - 발령 이력 조회 (추후 구현)
 *
 * **권한:**
 * - hr.manage 권한 필요 (HR, Super Admin만 접근 가능)
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import {
  Search,
  Loader,
  AlertCircle,
  CheckCircle,
  XCircle,
  CheckSquare,
  Square,
  UserCheck,
  Award,
  Clock,
  History,
  ChevronLeft,
  ChevronRight,
  Users,
} from 'lucide-react';
import { usePromotionCandidates } from '../../shared/hooks/usePromotionCandidates';
import { useOrgUnits } from '../../shared/hooks/useOrgUnits';
import { usePositionLevels } from '../../shared/hooks/usePositionLevels';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { BulkPromotionAppointmentModal } from './components/BulkPromotionAppointmentModal';
import { PersonnelAppointmentDetailModal } from './components/PersonnelAppointmentDetailModal';
import { formatYearsMonths } from '../../shared/utils/formatYearsMonths';  // V79
import { PromotionCandidate } from '../../shared/types/promotionCandidate';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { useAppointmentMutations, SaveAppointmentRequest } from '../../shared/hooks/useAppointmentMutations';
import { usePlannedPromotionCount, usePlannedAppointments } from '../../shared/hooks/usePersonnelAppointments';
import { APPOINTMENT_TYPE_LABELS, APPOINTMENT_STATUS_LABELS } from '../../shared/types/personnelAppointment';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { formatToday } from '../../shared/utils/dateUtils';
import { Pagination, paginateData } from '../../shared/components/Pagination';
import { useOrgUnitSubtreeFilter } from '../../shared/hooks/useOrgUnitSubtreeFilter';

/**
 * 탭 타입 정의
 */
type TabType = 'promotion-candidates' | 'appointment' | 'reservation' | 'history';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ComponentType<any>;
}

const TABS: TabConfig[] = [
  { id: 'promotion-candidates', label: '진급후보 산출', icon: Award },
  { id: 'appointment', label: '임명', icon: UserCheck },
  { id: 'reservation', label: '인사발령 예약', icon: Clock },
  { id: 'history', label: '히스토리', icon: History },
];

/**
 * 인사발령 페이지 컴포넌트
 */
export function HrPersonnelAppointmentPage() {
  const [activeTab, setActiveTab] = useState<TabType>('promotion-candidates');
  const [searchParams] = useSearchParams();

  // V62: URL ?tab= 파라미터로 초기 탭 지정 (대시보드 연동)
  const tabInitRef = useRef(false);
  useEffect(() => {
    if (tabInitRef.current) return;
    const t = searchParams.get('tab');
    if (t && TABS.some(tab => tab.id === t)) {
      tabInitRef.current = true;
      setActiveTab(t as TabType);
    }
  }, [searchParams]);

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>인사 발령</h1>
      </div>

      {/* 탭 네비게이션 */}
      <div style={styles.tabContainer}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tabButton,
                ...(isActive ? styles.tabButtonActive : {}),
              }}
            >
              <Icon size={18} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* 탭 컨텐츠 */}
      <div style={styles.tabContent}>
        {activeTab === 'promotion-candidates' && <PromotionCandidatesTab />}
        {activeTab === 'appointment' && <AppointmentTab />}
        {activeTab === 'reservation' && <ReservationTab />}
        {activeTab === 'history' && <HistoryTab />}
      </div>
    </div>
  );
}

/**
 * V60.2: 날짜 계산 및 인정근무연한 분해 로직은 usePromotionCandidates Hook으로 이동
 * - 리스트에서는 candidate.recognizedYearsBreakdown 직접 사용
 * - 재계산 금지 (판정/표시/정렬 불일치 방지)
 */

/**
 * 탭 1: 진급후보 산출 (기존 HrPromotionCandidatesPage 내용)
 */
function PromotionCandidatesTab() {
  // 권한 체크 (Chapter 5-4)
  const { hasPermission } = useAuthorization();
  const canManageAppointments = hasPermission('hr.manage');

  // V52: 기준연도 상태 (항상 1월 1일 기준)
  const getDefaultYear = () => {
    // 기본값: 다음 해 (PROMOTION_CONSTANTS.DEFAULT_REFERENCE_DATE와 동일한 로직)
    return new Date().getFullYear() + 1;
  };
  const [referenceYear, setReferenceYear] = useState(getDefaultYear);

  // referenceDate는 항상 해당 연도의 1월 1일로 계산
  const referenceDate = `${referenceYear}-01-01`;

  // 필터 상태
  const [orgUnitId, setOrgUnitId] = useState('all');
  const [positionId, setPositionId] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState<'all' | 'eligible' | 'ineligible'>(
    'all'
  );

  // 선택 상태 (Chapter 5-4)
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(new Set());

  // 모달 상태 (Chapter 5-4)
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // V58: 인사발령 상세 모달 상태
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<PromotionCandidate | null>(null);

  // 성공 메시지 (Chapter 5-4)
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // V73.2: 조직 subtree 필터 (공통 훅)
  const { orgUnits } = useOrgUnits({ isActive: true });
  const { descendantOrgUnitIds, orgDropdownOptions } = useOrgUnitSubtreeFilter(orgUnits, orgUnitId);

  // 데이터 조회 — V73.1: orgUnitIds 배열 전달
  const { candidates, isLoading, error, refetch } = usePromotionCandidates({
    referenceDate,
    orgUnitIds: descendantOrgUnitIds,
    orgUnitId: descendantOrgUnitIds ? undefined : (orgUnitId === 'all' ? undefined : orgUnitId),
    positionId: positionId === 'all' ? undefined : positionId,
    searchText,
    eligibilityFilter,
  });

  // V72: 예약된 진급 발령 건수 조회
  const { count: plannedPromotionCount, refetch: refetchPlannedCount } = usePlannedPromotionCount();

  // V89: 예약된 인사발령 조회 (httpsCallable 사용 - V88 훅 재사용)
  // V62: refetch 추가 — 등록 후 목록+보드 동시 갱신
  const { appointments: plannedAppointments, refetch: refetchPlannedAppointments } = usePlannedAppointments();

  // V72: 예약된 진급 정보가 추가된 candidates
  const [candidatesWithPlannedPromotions, setCandidatesWithPlannedPromotions] = useState<PromotionCandidate[]>([]);

  // V89: candidates 변경 시 예약된 진급 정보 조회 (usePlannedAppointments 훅 결과 사용)
  // V100: 무한 렌더링 방지 - 빈 배열 참조 안정화
  useEffect(() => {
    if (candidates.length === 0) {
      // V100: 이미 빈 배열이면 상태 업데이트 스킵 (참조 변경으로 인한 무한 렌더 방지)
      setCandidatesWithPlannedPromotions(prev => prev.length === 0 ? prev : []);
      return;
    }

    // V89→V60.x: plannedAppointments에서 승진 관련 유형 필터링 (EARLY_PROMOTION + PROMOTION)
    const earlyPromotions = plannedAppointments.filter(
      (appt) => appt.appointmentType === 'EARLY_PROMOTION' || appt.appointmentType === 'PROMOTION'
    );

    // employeeId로 맵핑
    const plannedPromotionsMap = new Map<string, any>();
    earlyPromotions.forEach((appt) => {
      plannedPromotionsMap.set(appt.employeeId, {
        appointmentId: appt.id,
        effectiveDate: appt.effectiveDate,
        toPositionId: appt.toPositionId,
        toPositionName: (appt as any).toPositionName,
      });
    });

    // candidates에 plannedPromotion 추가
    const enrichedCandidates = candidates.map((candidate) => ({
      ...candidate,
      plannedPromotion: plannedPromotionsMap.get(candidate.employee.id),
    }));

    setCandidatesWithPlannedPromotions(enrichedCandidates);
  }, [candidates, plannedAppointments]);

  // 직위 목록 (필터용)
  const { positionLevels } = usePositionLevels({ status: 'active' });

  /**
   * 필터 초기화
   */
  const handleResetFilters = () => {
    setReferenceYear(getDefaultYear());
    setOrgUnitId('all');
    setPositionId('all');
    setSearchText('');
    setEligibilityFilter('all');
  };

  /**
   * V52: 연도 변경 핸들러
   * V72: 초기 기준 연도보다 이전으로는 이동 불가
   */
  const defaultYear = getDefaultYear();

  const handlePrevYear = () => {
    setReferenceYear(prev => {
      // 초기 기준 연도(현재 + 1)보다 이전으로는 못감
      if (prev <= defaultYear) {
        return prev;
      }
      return prev - 1;
    });
  };

  const handleNextYear = () => {
    setReferenceYear(prev => prev + 1);
  };

  /**
   * V58: 직원 클릭 시 인사발령 상세 모달 오픈 (페이지 이동 ❌)
   */
  const handleEmployeeClick = (candidate: PromotionCandidate) => {
    setSelectedCandidate(candidate);
    setIsDetailModalOpen(true);
  };

  /**
   * V58: 인사발령 상세 모달 저장 성공 핸들러
   */
  const handleDetailModalSuccess = () => {
    // 진급후보 리스트 자동 리프레시
    refetch();
    // V62: 예약 카운트 + 예약 목록 동시 갱신 (기존 누락)
    refetchPlannedCount();
    refetchPlannedAppointments();
    setSuccessMessage('직위 발령일이 성공적으로 저장되었습니다.');
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  /**
   * 전체 선택/해제
   */
  const handleSelectAll = () => {
    if (selectedCandidateIds.size === candidatesWithPlannedPromotions.length) {
      setSelectedCandidateIds(new Set());
    } else {
      setSelectedCandidateIds(new Set(candidatesWithPlannedPromotions.map(c => c.employee.id)));
    }
  };

  /**
   * 개별 선택/해제
   */
  const handleSelectCandidate = (employeeId: string) => {
    const newSet = new Set(selectedCandidateIds);
    if (newSet.has(employeeId)) {
      newSet.delete(employeeId);
    } else {
      newSet.add(employeeId);
    }
    setSelectedCandidateIds(newSet);
  };

  /**
   * "진급 발령 생성" 버튼 클릭
   */
  const handleBulkAppointment = () => {
    if (selectedCandidateIds.size === 0) {
      alert('진급 발령 처리할 대상을 먼저 선택해 주세요.');
      return;
    }
    setIsBulkModalOpen(true);
  };

  /**
   * 일괄 발령 성공 시
   */
  const handleBulkAppointmentSuccess = (successCount: number, failureCount: number) => {
    setSelectedCandidateIds(new Set());

    if (failureCount === 0) {
      setSuccessMessage(`총 ${successCount}건의 승진 발령이 성공적으로 생성되었습니다.`);
    } else {
      setSuccessMessage(
        `${successCount}건 성공, ${failureCount}건 실패했습니다. 실패한 발령은 수동으로 처리해주세요.`
      );
    }

    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);

    // 진급 후보 목록 갱신
    refetch();

    // V73: 예약 상태 즉시 갱신 (예약 카운트 refetch)
    refetchPlannedCount();
    // V62: 예약 목록도 refetch — 목록/보드 동시 갱신
    refetchPlannedAppointments();
  };

  /**
   * 진급 가능자 수 계산
   */
  const eligibleCount = candidatesWithPlannedPromotions.filter(c => c.meetsCriteria).length;
  const ineligibleCount = candidatesWithPlannedPromotions.length - eligibleCount;

  // 선택된 후보 객체 배열
  const selectedCandidates = candidatesWithPlannedPromotions.filter(c => selectedCandidateIds.has(c.employee.id));

  return (
    <div>
      {/* 성공 메시지 */}
      {successMessage && (
        <div style={promotionStyles.successAlert}>
          <CheckCircle size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      {/* 통계 카드 */}
      <div style={promotionStyles.statsContainer}>
        <div style={{ ...promotionStyles.statCard, borderLeftColor: 'var(--color-primary)' }}>
          <div style={promotionStyles.statLabel}>전체 후보</div>
          <div style={promotionStyles.statValue}>{candidates.length}명</div>
        </div>
        <div style={{ ...promotionStyles.statCard, borderLeftColor: '#4CAF50' }}>
          <div style={promotionStyles.statLabel}>진급 가능</div>
          <div style={{ ...promotionStyles.statValue, color: '#4CAF50' }}>{eligibleCount}명</div>
        </div>
        <div style={{ ...promotionStyles.statCard, borderLeftColor: '#9E9E9E' }}>
          <div style={promotionStyles.statLabel}>기준 미달</div>
          <div style={{ ...promotionStyles.statValue, color: '#9E9E9E' }}>{ineligibleCount}명</div>
        </div>
        {/* V72: 진급 예약 카드 */}
        <div style={{ ...promotionStyles.statCard, borderLeftColor: '#9C27B0' }}>
          <div style={promotionStyles.statLabel}>진급 예약</div>
          <div style={{ ...promotionStyles.statValue, color: '#9C27B0' }}>{plannedPromotionCount}건</div>
        </div>
        {canManageAppointments && selectedCandidateIds.size > 0 && (
          <div style={{ ...promotionStyles.statCard, borderLeftColor: '#2196F3' }}>
            <div style={promotionStyles.statLabel}>선택</div>
            <div style={{ ...promotionStyles.statValue, color: '#2196F3' }}>
              {selectedCandidateIds.size}명
            </div>
          </div>
        )}
      </div>

      {/* 필터 영역 */}
      <div style={promotionStyles.filterSection}>
        {/* V52: 기준연도 선택기 */}
        <div style={promotionStyles.filterItem}>
          <label style={promotionStyles.filterLabel}>
            <span>기준연도</span>
          </label>
          <div style={promotionStyles.yearSelector}>
            <button
              onClick={handlePrevYear}
              disabled={referenceYear <= defaultYear}
              style={{
                ...promotionStyles.yearButton,
                opacity: referenceYear <= defaultYear ? 0.3 : 1,
                cursor: referenceYear <= defaultYear ? 'not-allowed' : 'pointer',
              }}
              title={referenceYear <= defaultYear ? '최소 연도입니다' : '이전 연도'}
            >
              <ChevronLeft size={20} />
            </button>
            <span style={promotionStyles.yearDisplay}>{referenceYear}</span>
            <button
              onClick={handleNextYear}
              style={promotionStyles.yearButton}
              title="다음 연도"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* 조직 선택 */}
        <div style={promotionStyles.filterItem}>
          <label style={promotionStyles.filterLabel}>조직</label>
          <select
            value={orgUnitId}
            onChange={e => setOrgUnitId(e.target.value)}
            style={promotionStyles.select}
          >
            <option value="all">전체</option>
            {orgDropdownOptions.map((org: any) => (
              <option key={org.id} value={org.id}>
                {'　'.repeat(org.level || 0)}{org.name}
              </option>
            ))}
          </select>
        </div>

        {/* 직위 선택 */}
        <div style={promotionStyles.filterItem}>
          <label style={promotionStyles.filterLabel}>현재 직위</label>
          <select
            value={positionId}
            onChange={e => setPositionId(e.target.value)}
            style={promotionStyles.select}
          >
            <option value="all">전체</option>
            {positionLevels.map(pos => (
              <option key={pos.id} value={pos.id}>
                {pos.name}
              </option>
            ))}
          </select>
        </div>

        {/* 진급 가능 여부 필터 */}
        <div style={promotionStyles.filterItem}>
          <label style={promotionStyles.filterLabel}>진급 가능 여부</label>
          <select
            value={eligibilityFilter}
            onChange={e => setEligibilityFilter(e.target.value as any)}
            style={promotionStyles.select}
          >
            <option value="all">전체</option>
            <option value="eligible">가능</option>
            <option value="ineligible">불가</option>
          </select>
        </div>

        {/* 검색 */}
        <div style={{ ...promotionStyles.searchBox, flex: 1 }}>
          <Search size={20} style={{ color: 'var(--color-gray-400)' }} />
          <input
            type="text"
            placeholder="이름 검색..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={promotionStyles.searchInput}
          />
        </div>

        {/* 필터 초기화 */}
        {(searchText ||
          orgUnitId !== 'all' ||
          positionId !== 'all' ||
          eligibilityFilter !== 'all' ||
          referenceYear !== getDefaultYear()) && (
          <button style={promotionStyles.resetButton} onClick={handleResetFilters}>
            필터 초기화
          </button>
        )}
      </div>

      {/* 액션 바 */}
      {canManageAppointments && candidatesWithPlannedPromotions.length > 0 && (
        <div style={promotionStyles.actionBar}>
          <div style={promotionStyles.actionBarLeft}>
            {selectedCandidateIds.size > 0 && (
              <span style={promotionStyles.selectionCount}>
                선택: <strong>{selectedCandidateIds.size}</strong>명
              </span>
            )}
          </div>
          <div style={promotionStyles.actionBarRight}>
            {selectedCandidateIds.size > 0 && (
              <button style={promotionStyles.bulkAppointmentButton} onClick={handleBulkAppointment}>
                <UserCheck size={18} />
                <span>진급 발령 생성</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div style={promotionStyles.loadingBox}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '12px', color: 'var(--color-gray-600)' }}>
            진급 후보를 계산하는 중...
          </p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && !isLoading && (
        <div style={promotionStyles.errorBox}>
          <AlertCircle size={32} color="var(--color-error)" />
          <p style={{ marginTop: '12px', color: 'var(--color-error)', fontWeight: '500' }}>
            진급 후보 계산에 실패했습니다.
          </p>
          <p style={{ marginTop: '4px', color: 'var(--color-gray-600)', fontSize: '14px' }}>
            {error.message}
          </p>
          <button style={promotionStyles.retryButton} onClick={() => refetch()}>
            다시 시도
          </button>
        </div>
      )}

      {/* 진급 후보 테이블 */}
      {!isLoading && !error && (
        <>
          {candidatesWithPlannedPromotions.length === 0 ? (
            <div style={promotionStyles.emptyBox}>
              <p style={{ color: 'var(--color-gray-600)' }}>
                {searchText ||
                orgUnitId !== 'all' ||
                positionId !== 'all' ||
                eligibilityFilter !== 'all'
                  ? '검색 결과가 없습니다.'
                  : '진급 후보가 없습니다.'}
              </p>
            </div>
          ) : (
            <div style={promotionStyles.tableContainer}>
              <table style={promotionStyles.table}>
                <thead>
                  <tr>
                    {canManageAppointments && (
                      <th style={{ ...promotionStyles.th, width: '50px' }}>
                        <button
                          onClick={handleSelectAll}
                          style={promotionStyles.checkboxButton}
                          title={
                            selectedCandidateIds.size === candidatesWithPlannedPromotions.length
                              ? '전체 해제'
                              : '전체 선택'
                          }
                        >
                          {selectedCandidateIds.size === candidatesWithPlannedPromotions.length ? (
                            <CheckSquare size={20} color="var(--color-primary)" />
                          ) : (
                            <Square size={20} color="var(--color-gray-400)" />
                          )}
                        </button>
                      </th>
                    )}
                    <th style={promotionStyles.th}>이름</th>
                    <th style={promotionStyles.th}>조직</th>
                    <th style={promotionStyles.th}>현재 직위</th>
                    <th style={promotionStyles.th}>직위 발령일</th>
                    <th style={promotionStyles.th}>직위 연차</th>
                    {/* V66: 인정근무연한 헤더 - 가산 포함 */}
                    <th style={promotionStyles.th}>
                      <div>인정근무연한</div>
                      <div style={{ fontSize: '11px', fontWeight: 'normal', color: 'var(--color-gray-500)' }}>(학력+이전경력+근속+가산)</div>
                    </th>
                    <th style={promotionStyles.th}>다음 직위</th>
                    <th style={promotionStyles.th}>진급 기준</th>
                    <th style={promotionStyles.th}>충족여부</th>
                    <th style={promotionStyles.th}>비고</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatesWithPlannedPromotions.map(candidate => (
                    <tr key={candidate.employee.id} style={promotionStyles.tr}>
                      {canManageAppointments && (
                        <td
                          style={promotionStyles.td}
                          onClick={e => {
                            e.stopPropagation();
                            handleSelectCandidate(candidate.employee.id);
                          }}
                        >
                          <button style={promotionStyles.checkboxButton}>
                            {selectedCandidateIds.has(candidate.employee.id) ? (
                              <CheckSquare size={20} color="var(--color-primary)" />
                            ) : (
                              <Square size={20} color="var(--color-gray-400)" />
                            )}
                          </button>
                        </td>
                      )}
                      <td
                        style={{ ...promotionStyles.td, cursor: 'pointer' }}
                        onClick={() => handleEmployeeClick(candidate)}
                      >
                        <strong>{candidate.employee.nameKo}</strong>
                      </td>
                      <td style={promotionStyles.td}>{candidate.employee.orgUnitName || '-'}</td>
                      <td style={promotionStyles.td}>
                        <strong>{candidate.currentPosition.name}</strong>
                      </td>
                      {/* V60.2: 직위 발령일 - displayPositionStartDate 사용 */}
                      <td style={promotionStyles.td}>
                        {candidate.displayPositionStartDate || '-'}
                      </td>
                      {/* V54: 직위 연차 - (+숫자) 표기 제거 */}
                      {/* V80.1: 직위 발령일 없으면 '-' 표시 (엑셀 주입 데이터 혼동 방지) */}
                      <td style={promotionStyles.td}>
                        {candidate.displayPositionStartDate
                          ? <><strong>{candidate.positionYears}</strong>년</>
                          : '-'}
                      </td>
                      {/* V66.1: 인정근무연한 - 합산값 앞(강조), breakdown 뒤(회색) */}
                      {/* V79: "N년 N개월" 표기 */}
                      <td style={promotionStyles.td}>
                        <strong>{formatYearsMonths(candidate.recognizedYears)}</strong>
                        <span style={{ marginLeft: '4px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                          ({candidate.recognizedYearsBreakdown.educationYears}+{candidate.recognizedYearsBreakdown.priorCareerYears}+{candidate.recognizedYearsBreakdown.workYears}+{candidate.recognizedYearsBreakdown.promotionBonusYears})
                        </span>
                      </td>
                      <td style={promotionStyles.td}>
                        {candidate.nextPosition ? (
                          <strong>{candidate.nextPosition.name}</strong>
                        ) : (
                          <span style={{ color: 'var(--color-gray-500)' }}>-</span>
                        )}
                      </td>
                      <td style={promotionStyles.td}>
                        {candidate.nextPosition && candidate.nextPosition.requiredRecognizedYearsFromHire !== null ? (
                          <span>
                            {candidate.nextPosition.requiredRecognizedYearsFromHire}년 이상
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-gray-500)' }}>-</span>
                        )}
                      </td>
                      {/* V53: 충족여부 - 진급대상(녹색) / 미충족(회색) / V72: 진급예약(보라색) */}
                      <td style={promotionStyles.td}>
                        {candidate.plannedPromotion ? (
                          <span
                            style={{
                              ...promotionStyles.badge,
                              backgroundColor: '#F3E5F5',
                              color: '#9C27B0',
                            }}
                          >
                            <Clock size={14} />
                            진급예약
                          </span>
                        ) : candidate.meetsCriteria ? (
                          <span
                            style={{
                              ...promotionStyles.badge,
                              backgroundColor: '#E8F5E9',
                              color: '#4CAF50',
                            }}
                          >
                            <CheckCircle size={14} />
                            진급대상
                          </span>
                        ) : (
                          <span
                            style={{
                              ...promotionStyles.badge,
                              backgroundColor: '#F5F5F5',
                              color: '#9E9E9E',
                            }}
                          >
                            <XCircle size={14} />
                            미충족
                          </span>
                        )}
                      </td>
                      <td style={promotionStyles.td}>
                        <span style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                          {candidate.plannedPromotion
                            ? `${candidate.plannedPromotion.effectiveDate} 진급 예정`
                            : candidate.reason}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 결과 개수 */}
          {candidatesWithPlannedPromotions.length > 0 && (
            <div style={promotionStyles.footer}>
              <p style={{ color: 'var(--color-gray-600)', fontSize: '14px' }}>
                총 <strong>{candidatesWithPlannedPromotions.length}</strong>명의 진급 후보
                <span style={{ marginLeft: '16px', color: '#4CAF50' }}>
                  진급 가능: <strong>{eligibleCount}</strong>명
                </span>
                <span style={{ marginLeft: '16px', color: '#9E9E9E' }}>
                  기준 미달: <strong>{ineligibleCount}</strong>명
                </span>
                {canManageAppointments && selectedCandidateIds.size > 0 && (
                  <span style={{ marginLeft: '16px', color: '#2196F3' }}>
                    선택: <strong>{selectedCandidateIds.size}</strong>명
                  </span>
                )}
              </p>
            </div>
          )}
        </>
      )}

      {/* 일괄 승진 발령 모달 */}
      <BulkPromotionAppointmentModal
        isOpen={isBulkModalOpen}
        candidates={selectedCandidates}
        referenceDate={referenceDate}
        onClose={() => setIsBulkModalOpen(false)}
        onSuccess={handleBulkAppointmentSuccess}
      />

      {/* V58: 인사발령 상세 모달 */}
      <PersonnelAppointmentDetailModal
        isOpen={isDetailModalOpen}
        candidate={selectedCandidate}
        referenceDate={referenceDate}
        onClose={() => setIsDetailModalOpen(false)}
        onSuccess={handleDetailModalSuccess}
      />
    </div>
  );
}

/**
 * 탭 2: 임명 (V72)
 */
/**
 * V74.3: 임명 탭 - 진급후보 탭과 동일한 구조
 *
 * **UX 개편:**
 * 1. 초기 상태: 필터/검색 영역 + "검색 후 조회하세요" 빈 상태
 * 2. 검색 실행 후: 테이블 형태의 직원 리스트 표시
 * 3. 행 클릭 시: 임명 폼 모달 표시
 *
 * **Firestore 쿼리 정책:**
 * - 초기 진입 시: 쿼리 0회
 * - 검색 버튼 클릭 시: 직원 목록 로드
 */
function AppointmentTab() {
  // Hooks (마스터 데이터만 - 직원은 검색 시 로드)
  const { orgUnits } = useOrgUnits({ isActive: true });
  const { positionLevels } = usePositionLevels({ status: 'active' });
  const { jobTitles } = useJobTitles({ status: 'active' });
  const { saveAppointment, isSubmitting } = useAppointmentMutations();

  // V74.3: 검색 상태 관리 (초기에는 employees 로드하지 않음)
  const [searchText, setSearchText] = useState('');
  const [orgUnitFilter, setOrgUnitFilter] = useState('all');

  // V73.2: 조직 subtree 필터 (공통 훅)
  const { descendantOrgUnitIds, orgDropdownOptions: tab2OrgOptions } = useOrgUnitSubtreeFilter(orgUnits, orgUnitFilter);
  const [positionFilter, setPositionFilter] = useState('all');
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchError, setSearchError] = useState<Error | null>(null);

  // 선택된 직원 및 폼 상태
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentType, setAppointmentType] = useState<'POSITION_APPOINTMENT' | 'TITLE_APPOINTMENT'>('POSITION_APPOINTMENT');
  const [targetId, setTargetId] = useState('');
  const [executionMode, setExecutionMode] = useState<'EXECUTED' | 'PLANNED' | 'PLANNED_DATE'>('EXECUTED');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [effectiveYear, setEffectiveYear] = useState(new Date().getFullYear() + 1);
  const [reason, setReason] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 임명직 필터링 (requiredRecognizedYearsFromHire === null && requiredYearsForNextPosition === null)
  // V73: "사원" 직위는 임명 대상에서 제외
  const appointablePositions = positionLevels.filter(pos =>
    pos.requiredRecognizedYearsFromHire === null &&
    pos.requiredYearsForNextPosition === null &&
    pos.name !== '사원'
  );

  /**
   * V90: 검색 실행 핸들러 (디버깅 강화 + status 필터 제거)
   *
   * - getEmployees callable 호출 (HR/ADMIN only)
   * - status 파라미터 제거: 퇴사자 제외는 백엔드에서 처리
   * - permission-denied 에러 시 명확한 UX 제공
   */
  const handleSearch = async () => {
    if (!functions) {
      setSearchError(new Error('Firebase Functions가 초기화되지 않았습니다.'));
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    // V90: 호출 파라미터 로깅 — V73.1: orgUnitIds 배열 전달
    const params: Record<string, any> = {
      searchText: searchText.trim() || undefined,
    };
    if (descendantOrgUnitIds) {
      params.orgUnitIds = descendantOrgUnitIds;
    } else if (orgUnitFilter !== 'all') {
      params.orgUnitId = orgUnitFilter;
    }
    try {
      const getEmployeesFn = httpsCallable<
        { searchText?: string; orgUnitId?: string; status?: string },
        { success: boolean; employees: any[] }
      >(functions!, 'getEmployees');

      const result = await getEmployeesFn(params);

      if (!result.data.success) {
        throw new Error('직원 목록 조회 실패');
      }

      // V90: 클라이언트에서 terminated 제외 (임명은 재직자만)
      let employees = result.data.employees.filter((e: any) => e.status !== 'terminated');

      // 직위 필터 (클라이언트 추가 필터링)
      if (positionFilter !== 'all') {
        employees = employees.filter((e: any) =>
          e.positionLevelId === positionFilter || e.positionId === positionFilter
        );
      }

      // 결과 매핑
      const results = employees.map((e: any) => ({
        id: e.id,
        nameKo: e.nameKo || '-',
        employeeNumber: e.employeeNumber || '-',
        email: e.email || '-',
        positionId: e.positionLevelId || e.positionId,
        positionTitle: e.positionTitle || '-',
        orgUnitId: e.orgUnitId,
        orgUnitName: e.orgUnitName || '-',
        jobTitleId: e.jobTitleId,
        jobTitleName: e.jobTitle || '-',
      }));

      // 이름순 정렬
      results.sort((a: any, b: any) => (a.nameKo || '').localeCompare(b.nameKo || '', 'ko'));

      setSearchResults(results);
      setHasSearched(true);
    } catch (err: any) {
      // V90: 에러 상세 로깅
      console.error('[V90] 직원 검색 실패:', {
        code: err.code,
        message: err.message,
        details: err.details,
      });

      // V90: permission-denied 에러 처리 강화
      if (err.code === 'permission-denied' || err.message?.includes('permission') || err.message?.includes('권한')) {
        setSearchError(new Error('권한이 없습니다. 인사 담당자 또는 관리자만 직원 목록을 조회할 수 있습니다.'));
      } else if (err.code === 'unauthenticated') {
        setSearchError(new Error('로그인이 필요합니다. 다시 로그인해 주세요.'));
      } else {
        setSearchError(new Error(err.message || '직원 목록 조회 중 오류가 발생했습니다.'));
      }
    } finally {
      setIsSearching(false);
    }
  };

  // V74.3: 필터 초기화
  const handleResetFilters = () => {
    setSearchText('');
    setOrgUnitFilter('all');
    setPositionFilter('all');
    setHasSearched(false);
    setSearchResults([]);
    setSearchError(null);
  };

  // V74.3: 직원 행 클릭 → 임명 모달 열기
  const handleRowClick = (emp: any) => {
    setSelectedEmployee(emp);
    setShowAppointmentModal(true);
    // 폼 초기화
    setAppointmentType('POSITION_APPOINTMENT');
    setTargetId('');
    setExecutionMode('EXECUTED');
    setEffectiveDate('');
    setEffectiveYear(new Date().getFullYear() + 1);
    setReason('');
    setErrorMessage(null);
  };

  // 임명 모달 닫기
  const handleCloseModal = () => {
    setShowAppointmentModal(false);
    setSelectedEmployee(null);
    setErrorMessage(null);
  };

  // 저장 핸들러
  const handleSave = async () => {
    if (!selectedEmployee) return;

    if (!targetId) {
      setErrorMessage(`${appointmentType === 'POSITION_APPOINTMENT' ? '직위' : '직책'}를 선택해주세요.`);
      return;
    }

    if (!effectiveDate && (executionMode === 'EXECUTED' || executionMode === 'PLANNED_DATE')) {
      setErrorMessage('발령일을 입력해주세요.');
      return;
    }

    // V61: 날짜 선택 예약은 미래 날짜만 가능
    if (executionMode === 'PLANNED_DATE') {
      const today = new Date().toISOString().split('T')[0];
      if (effectiveDate <= today) {
        setErrorMessage('예약 발령일은 미래 날짜만 가능합니다.');
        return;
      }
    }

    try {
      const request: SaveAppointmentRequest = {
        employeeId: selectedEmployee.id,
        appointmentType,
        effectiveDate: executionMode === 'PLANNED' ? `${effectiveYear}-01-01` : effectiveDate,
        status: executionMode === 'EXECUTED' ? 'EXECUTED' : 'PLANNED',
        reason: reason || undefined,
      };

      if (appointmentType === 'POSITION_APPOINTMENT') {
        request.toPositionId = targetId;
      } else {
        request.toTitleId = targetId;
      }

      const result = await saveAppointment(request);

      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(null), 5000);

      // 모달 닫기 및 목록 갱신
      handleCloseModal();
      handleSearch(); // 목록 새로고침
    } catch (error: any) {
      setErrorMessage(error.message || '임명 저장에 실패했습니다.');
    }
  };

  return (
    <div>
      {/* 성공 메시지 */}
      {successMessage && (
        <div style={promotionStyles.successAlert}>
          <CheckCircle size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      {/* V74.3: 필터/검색 영역 (진급후보 탭과 동일 스타일) */}
      <div style={promotionStyles.filterSection}>
        {/* 조직 선택 */}
        <div style={promotionStyles.filterItem}>
          <label style={promotionStyles.filterLabel}>조직</label>
          <select
            value={orgUnitFilter}
            onChange={e => setOrgUnitFilter(e.target.value)}
            style={promotionStyles.select}
          >
            <option value="all">전체</option>
            {tab2OrgOptions.map((org: any) => (
                <option key={org.id} value={org.id}>
                  {'　'.repeat(org.level || 0)}{org.name}
                </option>
              ))}
          </select>
        </div>

        {/* 직위 선택 */}
        <div style={promotionStyles.filterItem}>
          <label style={promotionStyles.filterLabel}>직위</label>
          <select
            value={positionFilter}
            onChange={e => setPositionFilter(e.target.value)}
            style={promotionStyles.select}
          >
            <option value="all">전체</option>
            {positionLevels.map(pos => (
              <option key={pos.id} value={pos.id}>
                {pos.name}
              </option>
            ))}
          </select>
        </div>

        {/* 검색 */}
        <div style={{ ...promotionStyles.searchBox, flex: 1 }}>
          <Search size={20} style={{ color: 'var(--color-gray-400)' }} />
          <input
            type="text"
            placeholder="이름, 사번, 이메일 검색..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={promotionStyles.searchInput}
          />
        </div>

        {/* 검색 버튼 */}
        <button
          onClick={handleSearch}
          disabled={isSearching}
          style={{
            ...promotionStyles.bulkAppointmentButton,
            opacity: isSearching ? 0.6 : 1,
          }}
        >
          {isSearching ? (
            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Search size={18} />
          )}
          <span>검색</span>
        </button>

        {/* 필터 초기화 */}
        {(searchText || orgUnitFilter !== 'all' || positionFilter !== 'all' || hasSearched) && (
          <button style={promotionStyles.resetButton} onClick={handleResetFilters}>
            초기화
          </button>
        )}
      </div>

      {/* 검색 중 */}
      {isSearching && (
        <div style={promotionStyles.loadingBox}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '12px', color: 'var(--color-gray-600)' }}>
            직원을 검색하는 중...
          </p>
        </div>
      )}

      {/* 에러 상태 */}
      {searchError && !isSearching && (
        <div style={promotionStyles.errorBox}>
          <AlertCircle size={32} color="var(--color-error)" />
          <p style={{ marginTop: '12px', color: 'var(--color-error)', fontWeight: '500' }}>
            직원 검색에 실패했습니다.
          </p>
          <p style={{ marginTop: '4px', color: 'var(--color-gray-600)', fontSize: '14px' }}>
            {searchError.message}
          </p>
          <button style={promotionStyles.retryButton} onClick={handleSearch}>
            다시 시도
          </button>
        </div>
      )}

      {/* 검색 전: 빈 상태 안내 */}
      {!hasSearched && !isSearching && !searchError && (
        <div style={promotionStyles.emptyBox}>
          <Search size={48} style={{ color: 'var(--color-gray-300)', marginBottom: '16px' }} />
          <p style={{ color: 'var(--color-gray-600)', fontWeight: '500' }}>
            검색/필터 후 조회하세요
          </p>
          <p style={{ color: 'var(--color-gray-400)', fontSize: '14px', marginTop: '8px' }}>
            조직, 직위를 선택하거나 이름을 입력한 후 검색 버튼을 클릭하세요.
          </p>
        </div>
      )}

      {/* 검색 결과: 테이블 */}
      {hasSearched && !isSearching && !searchError && (
        <>
          {searchResults.length === 0 ? (
            <div style={promotionStyles.emptyBox}>
              <p style={{ color: 'var(--color-gray-600)' }}>검색 결과가 없습니다.</p>
            </div>
          ) : (
            <div style={promotionStyles.tableContainer}>
              <div style={{ marginBottom: '12px', color: 'var(--color-gray-600)', fontSize: '14px' }}>
                검색 결과: <strong>{searchResults.length}</strong>명
              </div>
              <table style={promotionStyles.table}>
                <thead>
                  <tr>
                    <th style={promotionStyles.th}>이름</th>
                    <th style={promotionStyles.th}>사번</th>
                    <th style={promotionStyles.th}>조직</th>
                    <th style={promotionStyles.th}>직위</th>
                    <th style={promotionStyles.th}>직책</th>
                    <th style={promotionStyles.th}>이메일</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map(emp => (
                    <tr
                      key={emp.id}
                      style={{ ...promotionStyles.tr, cursor: 'pointer' }}
                      onClick={() => handleRowClick(emp)}
                    >
                      <td style={promotionStyles.td}>
                        <strong>{emp.nameKo}</strong>
                      </td>
                      <td style={promotionStyles.td}>{emp.employeeNumber}</td>
                      <td style={promotionStyles.td}>{emp.orgUnitName}</td>
                      <td style={promotionStyles.td}>{emp.positionTitle}</td>
                      <td style={promotionStyles.td}>{emp.jobTitleName || '-'}</td>
                      <td style={promotionStyles.td}>{emp.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* V74.3: 임명 모달 */}
      {showAppointmentModal && selectedEmployee && (
        <ModalOverlay style={appointmentStyles.modalOverlay} onClose={handleCloseModal}>
          <div style={appointmentStyles.modalContent} onClick={e => e.stopPropagation()}>
            {/* 모달 헤더 */}
            <div style={appointmentStyles.modalHeader}>
              <h3 style={appointmentStyles.modalTitle}>임명 처리</h3>
              <button onClick={handleCloseModal} style={appointmentStyles.modalCloseButton}>
                <XCircle size={24} />
              </button>
            </div>

            {/* 에러 메시지 */}
            {errorMessage && (
              <div style={appointmentStyles.errorAlert}>
                <AlertCircle size={20} />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* 선택된 직원 정보 */}
            <div style={appointmentStyles.selectedEmployeeCard}>
              <div style={appointmentStyles.selectedEmployeeName}>{selectedEmployee.nameKo}</div>
              <div style={appointmentStyles.selectedEmployeeInfo}>
                {selectedEmployee.employeeNumber} • {selectedEmployee.positionTitle} • {selectedEmployee.orgUnitName}
              </div>
            </div>

            {/* 임명 종류 */}
            <div style={appointmentStyles.section}>
              <label style={appointmentStyles.label}>
                <span style={appointmentStyles.labelText}>임명 종류</span>
                <span style={appointmentStyles.required}>*</span>
              </label>
              <div style={appointmentStyles.radioGroup}>
                <label style={appointmentStyles.radioLabel}>
                  <input
                    type="radio"
                    checked={appointmentType === 'POSITION_APPOINTMENT'}
                    onChange={() => {
                      setAppointmentType('POSITION_APPOINTMENT');
                      setTargetId('');
                    }}
                    style={appointmentStyles.radio}
                  />
                  <span>직위 임명 (임명직만)</span>
                </label>
                <label style={appointmentStyles.radioLabel}>
                  <input
                    type="radio"
                    checked={appointmentType === 'TITLE_APPOINTMENT'}
                    onChange={() => {
                      setAppointmentType('TITLE_APPOINTMENT');
                      setTargetId('');
                    }}
                    style={appointmentStyles.radio}
                  />
                  <span>직책 임명</span>
                </label>
              </div>
            </div>

            {/* 대상 선택 */}
            <div style={appointmentStyles.section}>
              <label style={appointmentStyles.label}>
                <span style={appointmentStyles.labelText}>
                  {appointmentType === 'POSITION_APPOINTMENT' ? '임명 직위' : '임명 직책'}
                </span>
                <span style={appointmentStyles.required}>*</span>
              </label>
              <select
                value={targetId}
                onChange={e => setTargetId(e.target.value)}
                style={appointmentStyles.select}
              >
                <option value="">선택하세요</option>
                {appointmentType === 'POSITION_APPOINTMENT'
                  ? appointablePositions.map(pos => (
                      <option key={pos.id} value={pos.id}>
                        {pos.name}
                      </option>
                    ))
                  : jobTitles.map(title => (
                      <option key={title.id} value={title.id}>
                        {title.name}
                      </option>
                    ))}
              </select>
            </div>

            {/* 실행 방식 */}
            <div style={appointmentStyles.section}>
              <label style={appointmentStyles.label}>
                <span style={appointmentStyles.labelText}>실행 방식</span>
                <span style={appointmentStyles.required}>*</span>
              </label>
              <div style={appointmentStyles.radioGroup}>
                <label style={appointmentStyles.radioLabel}>
                  <input
                    type="radio"
                    checked={executionMode === 'EXECUTED'}
                    onChange={() => setExecutionMode('EXECUTED')}
                    style={appointmentStyles.radio}
                  />
                  <span>즉시 실행</span>
                </label>
                <label style={appointmentStyles.radioLabel}>
                  <input
                    type="radio"
                    checked={executionMode === 'PLANNED'}
                    onChange={() => setExecutionMode('PLANNED')}
                    style={appointmentStyles.radio}
                  />
                  <span>1/1 예약</span>
                </label>
                <label style={appointmentStyles.radioLabel}>
                  <input
                    type="radio"
                    checked={executionMode === 'PLANNED_DATE'}
                    onChange={() => setExecutionMode('PLANNED_DATE')}
                    style={appointmentStyles.radio}
                  />
                  <span>날짜 선택 예약</span>
                </label>
              </div>
            </div>

            {/* 날짜 입력 */}
            <div style={appointmentStyles.section}>
              <label style={appointmentStyles.label}>
                <span style={appointmentStyles.labelText}>발령일</span>
                <span style={appointmentStyles.required}>*</span>
              </label>
              {executionMode === 'PLANNED' ? (
                <div style={appointmentStyles.yearSelector}>
                  <button
                    onClick={() => setEffectiveYear(prev => prev - 1)}
                    style={appointmentStyles.yearButton}
                    disabled={effectiveYear <= new Date().getFullYear() + 1}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span style={appointmentStyles.yearDisplay}>{effectiveYear}년 1월 1일</span>
                  <button
                    onClick={() => setEffectiveYear(prev => prev + 1)}
                    style={appointmentStyles.yearButton}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              ) : (
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={e => setEffectiveDate(e.target.value)}
                  style={appointmentStyles.dateInput}
                />
              )}
            </div>

            {/* 사유 입력 */}
            <div style={appointmentStyles.section}>
              <label style={appointmentStyles.label}>
                <span style={appointmentStyles.labelText}>임명 사유</span>
                <span style={{ color: 'var(--color-gray-500)', fontSize: '13px' }}>(선택)</span>
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="임명 사유를 입력하세요..."
                style={appointmentStyles.textarea}
                rows={3}
              />
            </div>

            {/* 버튼 그룹 */}
            <div style={appointmentStyles.modalButtonGroup}>
              <button onClick={handleCloseModal} style={appointmentStyles.cancelButton}>
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={isSubmitting || !targetId}
                style={{
                  ...appointmentStyles.submitButton,
                  ...(isSubmitting || !targetId ? appointmentStyles.submitButtonDisabled : {}),
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>저장 중...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    <span>{executionMode === 'EXECUTED' ? '즉시 실행' : '예약 저장'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

/**
 * V73: 탭 3: 인사발령 예약
 *
 * **기능:**
 * - PLANNED 상태의 인사발령 예약 목록 조회
 * - 조기 진급, 직위 임명, 직책 임명 예약 표시
 * - 취소 기능 (status='PLANNED' AND effectiveDate > 오늘)
 *
 * **테이블 컬럼:**
 * 1. 직원명
 * 2. 발령 유형 (조기 진급/직위 임명/직책 임명)
 * 3. 발령일
 * 4. 변경 내용 (직위/직책 변경 정보)
 * 5. 등록일
 * 6. 등록자
 * 7. 상태 (예약)
 * 8. 취소 버튼
 */
function ReservationTab() {
  const { appointments, isLoading, error, refetch } = usePlannedAppointments();
  const { cancelAppointment, isSubmitting } = useAppointmentMutations();

  // V62: 요약건을 표시 목록(appointments) 기반으로 집계 — 회사 스코프 자동 일치
  const appointmentCounts = useMemo(() => {
    const counts = { earlyPromotion: 0, promotion: 0, positionAppointment: 0, titleAppointment: 0 };
    for (const a of appointments) {
      switch (a.appointmentType) {
        case 'EARLY_PROMOTION': counts.earlyPromotion++; break;
        case 'PROMOTION': counts.promotion++; break;
        case 'POSITION_APPOINTMENT': counts.positionAppointment++; break;
        case 'TITLE_APPOINTMENT': counts.titleAppointment++; break;
      }
    }
    return counts;
  }, [appointments]);

  // V74: 취소 후 목록 갱신
  const handleRefreshAll = () => {
    refetch();
  };

  // 취소 모달 상태
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const today = formatToday();

  /**
   * 취소 버튼 활성화 여부 판단
   *
   * **조건:**
   * - status === 'PLANNED'
   * - effectiveDate > 오늘 (KST)
   */
  const isCancelable = (effectiveDate: string, status?: string): boolean => {
    return status === 'PLANNED' && effectiveDate > today;
  };

  /**
   * 취소 모달 열기
   */
  const handleOpenCancelModal = (appointmentId: string) => {
    setSelectedAppointmentId(appointmentId);
    setCancelReason('');
    setShowCancelModal(true);
  };

  /**
   * 취소 모달 닫기
   */
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setSelectedAppointmentId(null);
    setCancelReason('');
  };

  /**
   * 인사발령 취소 실행
   */
  const handleCancelAppointment = async () => {
    if (!selectedAppointmentId) return;

    try {
      await cancelAppointment({
        appointmentId: selectedAppointmentId,
        cancelReason: cancelReason.trim() || undefined,
      });

      alert('인사발령 예약이 취소되었습니다.');
      handleCloseCancelModal();
      handleRefreshAll(); // V74: 목록 및 카운트 모두 갱신
    } catch (err: any) {
      alert(`취소 실패: ${err.message || '알 수 없는 오류'}`);
    }
  };

  // 로딩 중
  if (isLoading) {
    return (
      <div style={reservationStyles.loadingContainer}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={reservationStyles.loadingText}>예약 목록을 불러오는 중...</p>
      </div>
    );
  }

  // 에러
  if (error) {
    return (
      <div style={reservationStyles.errorContainer}>
        <AlertCircle size={48} color="var(--color-red-500)" />
        <p style={reservationStyles.errorText}>예약 목록을 불러오는 중 오류가 발생했습니다.</p>
        <p style={reservationStyles.errorDetail}>{error.message}</p>
      </div>
    );
  }

  // 빈 목록
  if (appointments.length === 0) {
    return (
      <div style={reservationStyles.emptyContainer}>
        <Clock size={64} color="var(--color-gray-300)" />
        <h3 style={reservationStyles.emptyTitle}>예약된 인사발령이 없습니다</h3>
        <p style={reservationStyles.emptyDescription}>
          조기 진급, 직위 임명, 직책 임명 예약을 등록하면 여기에 표시됩니다.
        </p>
      </div>
    );
  }

  return (
    <div style={reservationStyles.container}>
      {/* 헤더 */}
      <div style={reservationStyles.header}>
        <div style={reservationStyles.headerTitle}>
          <Clock size={20} color="var(--color-primary)" />
          <h2 style={reservationStyles.title}>예약 발령 목록 ({appointments.length}건)</h2>
          {/* V62: type별 카운트 뱃지 — 승진/조기진급 분리, 리스트 색상 일치 */}
          <div style={reservationStyles.countBadges}>
            {appointmentCounts.promotion > 0 && (
              <span style={{ ...reservationStyles.countBadge, backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                승진 {appointmentCounts.promotion}
              </span>
            )}
            {appointmentCounts.earlyPromotion > 0 && (
              <span style={{ ...reservationStyles.countBadge, backgroundColor: '#F3E5F5', color: '#9C27B0' }}>
                조기진급 {appointmentCounts.earlyPromotion}
              </span>
            )}
            {appointmentCounts.positionAppointment > 0 && (
              <span style={{ ...reservationStyles.countBadge, backgroundColor: '#E3F2FD', color: '#1976D2' }}>
                직위임명 {appointmentCounts.positionAppointment}
              </span>
            )}
            {appointmentCounts.titleAppointment > 0 && (
              <span style={{ ...reservationStyles.countBadge, backgroundColor: '#E8F5E9', color: '#388E3C' }}>
                직책임명 {appointmentCounts.titleAppointment}
              </span>
            )}
          </div>
        </div>
        <button onClick={handleRefreshAll} style={reservationStyles.refreshButton}>
          <Loader size={16} />
          <span>새로고침</span>
        </button>
      </div>

      {/* 테이블 */}
      <div style={reservationStyles.tableContainer}>
        <table style={reservationStyles.table}>
          <thead>
            <tr>
              <th style={reservationStyles.th}>직원명</th>
              <th style={reservationStyles.th}>발령 유형</th>
              <th style={reservationStyles.th}>발령일</th>
              <th style={reservationStyles.th}>변경 내용</th>
              <th style={reservationStyles.th}>등록일</th>
              <th style={reservationStyles.th}>등록자</th>
              <th style={reservationStyles.th}>상태</th>
              <th style={reservationStyles.th}>취소</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map(appointment => {
              const canCancel = isCancelable(appointment.effectiveDate, appointment.status);

              // 변경 내용 표시
              let changeDetail = '-';
              if (appointment.appointmentType === 'EARLY_PROMOTION' || appointment.appointmentType === 'PROMOTION') {
                changeDetail = `${appointment.fromPositionName || '-'} → ${appointment.toPositionName || '-'}`;
              } else if (appointment.appointmentType === 'POSITION_APPOINTMENT') {
                changeDetail = `직위: ${appointment.toPositionName || '-'}`;
              } else if (appointment.appointmentType === 'TITLE_APPOINTMENT') {
                changeDetail = `직책: ${appointment.toTitleName || '-'}`;
              }

              return (
                <tr key={appointment.id} style={reservationStyles.tr}>
                  <td style={reservationStyles.td}>{appointment.employeeName || '-'}</td>
                  <td style={reservationStyles.td}>
                    <span style={{
                      ...reservationStyles.badge,
                      ...(appointment.appointmentType === 'EARLY_PROMOTION'
                        ? { backgroundColor: '#F3E5F5', color: '#9C27B0' }
                        : appointment.appointmentType === 'PROMOTION'
                        ? { backgroundColor: '#E8F5E9', color: '#2E7D32' }
                        : appointment.appointmentType === 'POSITION_APPOINTMENT'
                        ? { backgroundColor: '#E3F2FD', color: '#1976D2' }
                        : appointment.appointmentType === 'TITLE_APPOINTMENT'
                        ? { backgroundColor: '#E8F5E9', color: '#388E3C' }
                        : {}),
                    }}>
                      {appointment.appointmentType
                        ? APPOINTMENT_TYPE_LABELS[appointment.appointmentType]
                        : '-'}
                    </span>
                  </td>
                  <td style={reservationStyles.td}>{appointment.effectiveDate}</td>
                  <td style={reservationStyles.td}>{changeDetail}</td>
                  <td style={reservationStyles.td}>
                    {appointment.createdAt
                      ? new Date(appointment.createdAt).toLocaleDateString('ko-KR')
                      : '-'}
                  </td>
                  <td style={reservationStyles.td}>{appointment.createdByName || '-'}</td>
                  <td style={reservationStyles.td}>
                    <span
                      style={{
                        ...reservationStyles.statusBadge,
                        ...reservationStyles.statusPlanned,
                      }}
                    >
                      {appointment.status ? APPOINTMENT_STATUS_LABELS[appointment.status] : '-'}
                    </span>
                  </td>
                  <td style={reservationStyles.td}>
                    {canCancel ? (
                      <button
                        onClick={() => handleOpenCancelModal(appointment.id)}
                        style={reservationStyles.cancelButton}
                      >
                        <XCircle size={16} />
                        <span>취소</span>
                      </button>
                    ) : (
                      <span style={reservationStyles.disabledText}>취소 불가</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 취소 확인 모달 */}
      {showCancelModal && (
        <ModalOverlay onClose={handleCloseCancelModal} style={reservationStyles.modalOverlay}>
          <div style={reservationStyles.modalContent}>
            <h3 style={reservationStyles.modalTitle}>인사발령 취소</h3>
            <p style={reservationStyles.modalDescription}>
              정말로 이 인사발령 예약을 취소하시겠습니까?
            </p>

            {/* 취소 사유 입력 */}
            <div style={reservationStyles.modalSection}>
              <label style={reservationStyles.modalLabel}>
                취소 사유 <span style={{ color: 'var(--color-gray-500)' }}>(선택, 최대 100자)</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="취소 사유를 입력하세요..."
                maxLength={100}
                rows={3}
                style={reservationStyles.modalTextarea}
              />
              <div style={reservationStyles.modalCharCount}>
                {cancelReason.length} / 100
              </div>
            </div>

            {/* 버튼 */}
            <div style={reservationStyles.modalButtons}>
              <button
                onClick={handleCloseCancelModal}
                style={reservationStyles.modalCancelButton}
                disabled={isSubmitting}
              >
                닫기
              </button>
              <button
                onClick={handleCancelAppointment}
                style={reservationStyles.modalConfirmButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>취소 중...</span>
                  </>
                ) : (
                  <>
                    <XCircle size={16} />
                    <span>발령 취소</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

/**
 * V29.24: 히스토리 항목 타입
 */
interface AppointmentHistoryItem {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  orgUnitName: string;
  appointmentType: string;
  effectiveDate: string;
  status: string;
  createdAt?: string;
  executedAt?: string;
  cancelledAt?: string;
  fromPositionName?: string;
  toPositionName?: string;
  fromTitleName?: string;
  toTitleName?: string;
  createdByName?: string;
  cancelReason?: string;
  changeDescription?: string;
}

/**
 * 탭 4: 히스토리 (V29.24)
 *
 * EXECUTED/CANCELED 상태의 인사발령 이력을 조회합니다.
 * - V29.21 스냅샷 우선 사용 (발령 당시 값 고정)
 * - V29.19 회사 스코프 필터링 적용
 */
function HistoryTab() {
  // 필터 상태
  const today = new Date();
  const sixMonthsAgo = new Date(today);
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  const [dateFrom, setDateFrom] = useState(sixMonthsAgo.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(today.toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState<'EXECUTED' | 'CANCELED' | 'ALL'>('EXECUTED');
  const [searchText, setSearchText] = useState('');
  const [orgUnitId, setOrgUnitId] = useState('all');

  // 데이터 상태
  const [items, setItems] = useState<AppointmentHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // V71: 페이지네이션
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 15;

  // 조직 목록 (V29.19 스코프 적용됨)
  const { orgUnits } = useOrgUnits();

  // V73.2: 공통 훅으로 descendant 조직 ID 계산
  const { descendantOrgUnitIds, orgDropdownOptions: tab4OrgOptions } = useOrgUnitSubtreeFilter(orgUnits, orgUnitId);

  /**
   * 히스토리 조회 함수
   */
  const fetchHistory = async (cursor?: string) => {
    if (!functions) return;

    const isLoadMore = !!cursor;
    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError(null);
    }

    try {
      const getPersonnelAppointmentHistory = httpsCallable<
        {
          dateFrom?: string;
          dateTo?: string;
          status?: 'EXECUTED' | 'CANCELED' | 'ALL';
          searchText?: string;
          orgUnitId?: string;
          orgUnitIds?: string[];
          limit?: number;
          cursor?: string;
        },
        {
          success: boolean;
          items: AppointmentHistoryItem[];
          nextCursor?: string;
          totalScanned: number;
        }
      >(functions, 'getPersonnelAppointmentHistory');

      const result = await getPersonnelAppointmentHistory({
        dateFrom,
        dateTo,
        status: statusFilter,
        searchText: searchText.trim() || undefined,
        orgUnitIds: descendantOrgUnitIds,
        orgUnitId: orgUnitId === 'all' ? undefined : orgUnitId,
        limit: 50,
        cursor,
      });

      if (isLoadMore) {
        setItems(prev => [...prev, ...result.data.items]);
      } else {
        setItems(result.data.items);
      }
      setNextCursor(result.data.nextCursor);
    } catch (err: any) {
      console.error('[HistoryTab] 조회 오류:', err);
      setError(err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // 필터 변경 시 자동 조회
  useEffect(() => {
    setHistoryPage(1);
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, statusFilter, orgUnitId]);

  // V71: 커서 기반 데이터 자동 로드 (V71.1: error 시 무한 루프 방지)
  useEffect(() => {
    if (nextCursor && !isLoading && !isLoadingMore && !error) {
      fetchHistory(nextCursor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextCursor, isLoading, isLoadingMore, error]);

  /**
   * 검색 실행
   */
  const handleSearch = () => {
    setHistoryPage(1);
    fetchHistory();
  };

  // 로딩 중
  if (isLoading) {
    return (
      <div style={historyStyles.loadingContainer}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={historyStyles.loadingText}>히스토리를 불러오는 중...</p>
      </div>
    );
  }

  // 에러
  if (error) {
    return (
      <div style={historyStyles.errorContainer}>
        <AlertCircle size={48} color="var(--color-red-500)" />
        <p style={historyStyles.errorText}>히스토리를 불러오는 중 오류가 발생했습니다.</p>
        <p style={historyStyles.errorDetail}>{error.message}</p>
        <button onClick={() => fetchHistory()} style={historyStyles.retryButton}>
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div style={historyStyles.container}>
      {/* 필터 영역 */}
      <div style={historyStyles.filterSection}>
        {/* 기간 필터 */}
        <div style={historyStyles.filterItem}>
          <label style={historyStyles.filterLabel}>시작일</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={historyStyles.dateInput}
          />
        </div>
        <div style={historyStyles.filterItem}>
          <label style={historyStyles.filterLabel}>종료일</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            style={historyStyles.dateInput}
          />
        </div>

        {/* 상태 필터 */}
        <div style={historyStyles.filterItem}>
          <label style={historyStyles.filterLabel}>상태</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as 'EXECUTED' | 'CANCELED' | 'ALL')}
            style={historyStyles.select}
          >
            <option value="EXECUTED">실행됨</option>
            <option value="CANCELED">취소됨</option>
            <option value="ALL">전체</option>
          </select>
        </div>

        {/* 조직 필터 — V73.1: 트리 순서 + 들여쓰기 */}
        <div style={historyStyles.filterItem}>
          <label style={historyStyles.filterLabel}>조직</label>
          <select
            value={orgUnitId}
            onChange={e => setOrgUnitId(e.target.value)}
            style={historyStyles.select}
          >
            <option value="all">전체 조직</option>
            {tab4OrgOptions.map((org: any) => (
                <option key={org.id} value={org.id}>
                  {'　'.repeat(org.level || 0)}{org.name}
                </option>
              ))}
          </select>
        </div>

        {/* 검색어 */}
        <div style={historyStyles.filterItem}>
          <label style={historyStyles.filterLabel}>검색</label>
          <div style={historyStyles.searchBox}>
            <Search size={18} color="var(--color-gray-400)" />
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              placeholder="이름 / 사번 검색"
              style={historyStyles.searchInput}
            />
          </div>
        </div>

        {/* 검색 버튼 */}
        <button onClick={handleSearch} style={historyStyles.searchButton}>
          <Search size={16} />
          조회
        </button>
      </div>

      {/* 결과 헤더 */}
      <div style={historyStyles.resultHeader}>
        <div style={historyStyles.headerTitle}>
          <History size={20} color="var(--color-primary)" />
          <h2 style={historyStyles.title}>발령 이력 ({items.length}건)</h2>
        </div>
        <button onClick={() => fetchHistory()} style={historyStyles.refreshButton}>
          <Loader size={16} />
          <span>새로고침</span>
        </button>
      </div>

      {/* 빈 목록 */}
      {items.length === 0 ? (
        <div style={historyStyles.emptyContainer}>
          <History size={64} color="var(--color-gray-300)" />
          <h3 style={historyStyles.emptyTitle}>조회 결과가 없습니다</h3>
          <p style={historyStyles.emptyDescription}>
            선택한 조건에 해당하는 인사발령 이력이 없습니다.
          </p>
        </div>
      ) : (
        <>
          {/* 테이블 */}
          <div style={historyStyles.tableContainer}>
            <table style={historyStyles.table}>
              <thead>
                <tr>
                  <th style={historyStyles.th}>발령일</th>
                  <th style={historyStyles.th}>상태</th>
                  <th style={historyStyles.th}>유형</th>
                  <th style={historyStyles.th}>직원명</th>
                  <th style={historyStyles.th}>사번</th>
                  <th style={historyStyles.th}>조직</th>
                  <th style={historyStyles.th}>변경 내용</th>
                  <th style={historyStyles.th}>등록자</th>
                </tr>
              </thead>
              <tbody>
                {paginateData(items, historyPage, HISTORY_PAGE_SIZE).map(item => (
                  <tr key={item.id} style={historyStyles.tr}>
                    <td style={historyStyles.td}>{item.effectiveDate}</td>
                    <td style={historyStyles.td}>
                      <span
                        style={{
                          ...historyStyles.statusBadge,
                          ...(item.status === 'EXECUTED'
                            ? historyStyles.statusExecuted
                            : historyStyles.statusCanceled),
                        }}
                      >
                        {APPOINTMENT_STATUS_LABELS[item.status as keyof typeof APPOINTMENT_STATUS_LABELS] || item.status}
                      </span>
                    </td>
                    <td style={historyStyles.td}>
                      <span style={historyStyles.typeBadge}>
                        {APPOINTMENT_TYPE_LABELS[item.appointmentType as keyof typeof APPOINTMENT_TYPE_LABELS] || item.appointmentType}
                      </span>
                    </td>
                    <td style={historyStyles.td}>{item.employeeName || '-'}</td>
                    <td style={historyStyles.td}>{item.employeeNumber || '-'}</td>
                    <td style={historyStyles.td}>{item.orgUnitName || '-'}</td>
                    <td style={historyStyles.td}>{item.changeDescription || '-'}</td>
                    <td style={historyStyles.td}>{item.createdByName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* V71: 페이지네이션 */}
          <Pagination
            currentPage={historyPage}
            totalItems={items.length}
            pageSize={HISTORY_PAGE_SIZE}
            onPageChange={setHistoryPage}
          />
        </>
      )}
    </div>
  );
}

/**
 * 메인 페이지 스타일
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  tabContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    borderBottom: '1px solid var(--color-gray-200)',
    paddingBottom: '0',
  },
  tabButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-600)',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '-1px',
  },
  tabButtonActive: {
    color: 'var(--color-primary)',
    borderBottom: '2px solid var(--color-primary)',
    fontWeight: '600',
  },
  tabContent: {
    minHeight: '400px',
  },
};

/**
 * Placeholder 탭 스타일
 */
const placeholderStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    textAlign: 'center' as const,
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    margin: '24px 0 12px 0',
  },
  description: {
    fontSize: '14px',
    color: 'var(--color-gray-500)',
    margin: 0,
  },
};

/**
 * 진급후보 탭 스타일
 */
const promotionStyles: Record<string, React.CSSProperties> = {
  successAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#e8f5e9',
    borderLeft: '4px solid #4caf50',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#2e7d32',
  },
  statsContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '12px 16px',
    borderLeft: '4px solid',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    minWidth: '120px',
    maxWidth: '150px',
  },
  statLabel: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    marginBottom: '6px',
    textAlign: 'center' as const,
    fontWeight: '500',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    textAlign: 'center' as const,
  },
  // V52: 연도 선택기 스타일
  yearSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '5px 8px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    minWidth: '120px',
  },
  yearButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    color: 'var(--color-gray-600)',
    transition: 'all 0.2s',
  },
  yearDisplay: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-800)',
    minWidth: '50px',
    textAlign: 'center' as const,
  },
  filterSection: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
    alignItems: 'flex-end',
  },
  filterItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  select: {
    padding: '10px 32px 10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '120px',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    minWidth: '300px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
  },
  resetButton: {
    padding: '10px 20px',
    fontSize: '14px',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid var(--color-gray-200)',
  },
  actionBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  actionBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectionCount: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
  },
  bulkAppointmentButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  checkboxButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  retryButton: {
    marginTop: '16px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyBox: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '16px',
    textAlign: 'center' as const,
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    backgroundColor: 'var(--color-gray-50)',
    borderBottom: '2px solid var(--color-gray-200)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    transition: 'background-color 0.2s',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
    whiteSpace: 'nowrap' as const,
    textAlign: 'center' as const,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    fontSize: '13px',
    fontWeight: '500',
    borderRadius: '12px',
  },
  footer: {
    marginTop: '16px',
    textAlign: 'center' as const,
  },
};

/**
 * 임명 탭 스타일 (V72)
 */
const appointmentStyles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  successAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#e8f5e9',
    borderLeft: '4px solid #4caf50',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#2e7d32',
  },
  errorAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#ffebee',
    borderLeft: '4px solid #f44336',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#c62828',
  },
  header: {
    marginBottom: '32px',
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: 0,
    marginBottom: '8px',
  },
  headerDescription: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    padding: '32px',
  },
  section: {
    marginBottom: '28px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '8px',
  },
  labelText: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
  },
  required: {
    color: 'var(--color-error)',
    fontSize: '14px',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    transition: 'border-color 0.2s',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
  },
  searchResults: {
    marginTop: '8px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    maxHeight: '300px',
    overflowY: 'auto' as const,
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  searchResultItem: {
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    borderBottom: '1px solid var(--color-gray-100)',
    backgroundColor: 'transparent',
    textAlign: 'left' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  searchResultName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    marginBottom: '4px',
  },
  searchResultInfo: {
    fontSize: '13px',
    color: 'var(--color-gray-600)',
  },
  selectedEmployee: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: 'var(--color-gray-50)',
    border: '1px solid var(--color-gray-200)',
    borderRadius: '8px',
  },
  selectedEmployeeName: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    marginBottom: '4px',
  },
  selectedEmployeeInfo: {
    fontSize: '13px',
    color: 'var(--color-gray-600)',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-gray-400)',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--color-gray-700)',
    cursor: 'pointer',
  },
  radio: {
    cursor: 'pointer',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    color: 'var(--color-gray-800)',
    cursor: 'pointer',
    outline: 'none',
  },
  infoText: {
    marginTop: '8px',
    fontSize: '13px',
    color: 'var(--color-gray-500)',
  },
  dateInput: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    color: 'var(--color-gray-800)',
    outline: 'none',
  },
  yearSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
  },
  yearButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    color: 'var(--color-gray-600)',
    transition: 'all 0.2s',
  },
  yearDisplay: {
    flex: 1,
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    textAlign: 'center' as const,
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    color: 'var(--color-gray-800)',
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid var(--color-gray-200)',
  },
  submitButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 32px',
    fontSize: '15px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButtonDisabled: {
    backgroundColor: 'var(--color-gray-300)',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  // V74.2: 2단 레이아웃 스타일
  twoColumnLayout: {
    display: 'flex',
    gap: '24px',
  },
  searchPanel: {
    flex: '0 0 400px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  formPanel: {
    flex: 1,
    minWidth: 0,
  },
  searchSection: {
    marginBottom: '8px',
  },
  searchButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  resultSection: {
    flex: 1,
    minHeight: '300px',
  },
  placeholderBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    backgroundColor: 'var(--color-gray-50)',
    borderRadius: '12px',
    border: '1px dashed var(--color-gray-300)',
    minHeight: '300px',
  },
  placeholderText: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--color-gray-500)',
    margin: 0,
    marginBottom: '8px',
    textAlign: 'center' as const,
  },
  placeholderSubtext: {
    fontSize: '14px',
    color: 'var(--color-gray-400)',
    margin: 0,
    textAlign: 'center' as const,
  },
  employeeList: {
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-200)',
    borderRadius: '12px',
    maxHeight: '400px',
    overflowY: 'auto' as const,
  },
  listHeader: {
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--color-gray-600)',
    backgroundColor: 'var(--color-gray-50)',
    borderBottom: '1px solid var(--color-gray-200)',
    position: 'sticky' as const,
    top: 0,
  },
  employeeListItem: {
    width: '100%',
    padding: '14px 16px',
    border: 'none',
    borderBottom: '1px solid var(--color-gray-100)',
    backgroundColor: 'transparent',
    textAlign: 'left' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  employeeListItemSelected: {
    backgroundColor: 'var(--color-primary-light)',
    borderLeft: '3px solid var(--color-primary)',
  },
  employeeListName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    marginBottom: '4px',
  },
  employeeListInfo: {
    fontSize: '13px',
    color: 'var(--color-gray-500)',
  },
  selectedEmployeeCard: {
    padding: '16px',
    backgroundColor: 'var(--color-primary-lighter)',
    border: '1px solid var(--color-primary-light)',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  selectedEmployeeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '13px',
    color: 'var(--color-gray-600)',
  },
  // V74.3: 모달 스타일
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-gray-400)',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid var(--color-gray-200)',
  },
  cancelButton: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'var(--color-gray-100)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

/**
 * V73: 예약 탭 스타일
 */
const reservationStyles: Record<string, React.CSSProperties> = {
  // 컨테이너
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    overflow: 'hidden',
  },

  // 헤더
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  // V74: type별 카운트 뱃지 스타일
  countBadges: {
    display: 'flex',
    gap: '8px',
    marginLeft: '16px',
  },
  countBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  // 테이블
  tableContainer: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '14px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--color-gray-600)',
    backgroundColor: 'var(--color-gray-50)',
    textAlign: 'left' as const,
    borderBottom: '1px solid var(--color-gray-200)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    borderBottom: '1px solid var(--color-gray-100)',
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: 'var(--color-gray-700)',
    whiteSpace: 'nowrap' as const,
  },

  // 뱃지
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'var(--color-blue-700)',
    backgroundColor: 'var(--color-blue-50)',
    borderRadius: '12px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '12px',
  },
  statusPlanned: {
    color: 'var(--color-orange-700)',
    backgroundColor: 'var(--color-orange-50)',
  },

  // 버튼
  cancelButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--color-red-600)',
    backgroundColor: 'white',
    border: '1px solid var(--color-red-300)',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  disabledText: {
    fontSize: '13px',
    color: 'var(--color-gray-400)',
  },

  // 로딩/에러/빈 상태
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    gap: '16px',
  },
  loadingText: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    gap: '12px',
  },
  errorText: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  errorDetail: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    textAlign: 'center' as const,
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    margin: '16px 0 8px 0',
  },
  emptyDescription: {
    fontSize: '14px',
    color: 'var(--color-gray-500)',
    margin: 0,
  },

  // 모달
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    margin: '0 0 12px 0',
  },
  modalDescription: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: '0 0 24px 0',
    lineHeight: 1.6,
  },
  modalSection: {
    marginBottom: '24px',
  },
  modalLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    marginBottom: '8px',
  },
  modalTextarea: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  modalCharCount: {
    fontSize: '12px',
    color: 'var(--color-gray-500)',
    marginTop: '4px',
    textAlign: 'right' as const,
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  modalCancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  modalConfirmButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-red-600)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

/**
 * V29.24: 히스토리 탭 스타일
 */
const historyStyles: Record<string, React.CSSProperties> = {
  // 컨테이너
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },

  // 필터 섹션
  filterSection: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
    alignItems: 'flex-end',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  filterItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
  },
  dateInput: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    outline: 'none',
    minWidth: '140px',
  },
  select: {
    padding: '10px 32px 10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '120px',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    minWidth: '200px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
    backgroundColor: 'transparent',
  },
  searchButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  // 결과 헤더
  resultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    backgroundColor: 'white',
    borderRadius: '12px 12px 0 0',
    border: '1px solid var(--color-gray-200)',
    borderBottom: 'none',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  // 테이블
  tableContainer: {
    overflowX: 'auto' as const,
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-200)',
    borderTop: 'none',
    borderRadius: '0 0 12px 12px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '14px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--color-gray-600)',
    backgroundColor: 'var(--color-gray-50)',
    textAlign: 'left' as const,
    borderBottom: '1px solid var(--color-gray-200)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    borderBottom: '1px solid var(--color-gray-100)',
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: 'var(--color-gray-700)',
    whiteSpace: 'nowrap' as const,
  },

  // 뱃지
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '12px',
  },
  statusExecuted: {
    color: 'var(--color-green-700)',
    backgroundColor: 'var(--color-green-50)',
  },
  statusCanceled: {
    color: 'var(--color-red-700)',
    backgroundColor: 'var(--color-red-50)',
  },
  typeBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'var(--color-blue-700)',
    backgroundColor: 'var(--color-blue-50)',
    borderRadius: '12px',
  },

  // 로딩/에러/빈 상태
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    gap: '16px',
  },
  loadingText: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    gap: '12px',
  },
  errorText: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  errorDetail: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  retryButton: {
    marginTop: '12px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    textAlign: 'center' as const,
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    margin: '16px 0 8px 0',
  },
  emptyDescription: {
    fontSize: '14px',
    color: 'var(--color-gray-500)',
    margin: 0,
  },

  // 더 보기 버튼
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '0 0 12px 12px',
    borderTop: '1px solid var(--color-gray-100)',
  },
  loadMoreButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-primary)',
    backgroundColor: 'white',
    border: '1px solid var(--color-primary)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
